import { cn } from '@/lib/utils'
import type { ReactNode } from 'react'

export function DataTable({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-[rgba(154,184,194,0.12)] bg-[#0f151a]',
        className,
      )}
    >
      <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Data table">{children}</div>
    </div>
  )
}

export function DataTableHead({ children }: { children: ReactNode }) {
  return (
    <thead>
      <tr className="border-b border-[rgba(154,184,194,0.12)] bg-[#0b1014]">
        {children}
      </tr>
    </thead>
  )
}

export function DataTableHeaderCell({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn(
        'px-4 py-3 text-left text-xs font-semibold tracking-[0.03em] text-[#a4b4bc]',
        className,
      )}
    >
      {children}
    </th>
  )
}

export function DataTableBody({ children }: { children: ReactNode }) {
  return <tbody className="divide-y divide-[rgba(154,184,194,0.08)]">{children}</tbody>
}

export function DataTableRow({
  children,
  onClick,
  className,
}: {
  children: ReactNode
  onClick?: () => void
  className?: string
}) {
  return (
    <tr
      onClick={onClick}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (event) => {
        if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault()
          onClick()
        }
      } : undefined}
      className={cn(
        'transition-colors hover:bg-[rgba(255,255,255,0.025)]',
        onClick && 'cursor-pointer',
        className,
      )}
    >
      {children}
    </tr>
  )
}

export function DataTableCell({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <td className={cn('px-4 py-3.5 text-sm text-[#f0f4f8]', className)}>
      {children}
    </td>
  )
}

export function EmptyState({ title, description }: { title: string; description?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div className="h-12 w-12 rounded-2xl bg-[rgba(64,219,241,0.08)] flex items-center justify-center mb-4 ring-1 ring-[rgba(64,219,241,0.15)]">
        <span className="text-[#40dbf1] text-lg">—</span>
      </div>
      <p className="text-sm font-semibold text-[#f0f4f8]">{title}</p>
      {description && <p className="text-xs text-[#93a5ad] mt-1 max-w-sm">{description}</p>}
    </div>
  )
}

export function LoadingRows({ cols = 4 }: { cols?: number }) {
  return (
    <tbody>
      {Array.from({ length: 5 }).map((_, i) => (
        <tr key={i} className="border-b border-[rgba(64,219,241,0.06)]">
          {Array.from({ length: cols }).map((__, j) => (
            <td key={j} className="px-4 py-4">
              <div className="h-4 rounded-md bg-[rgba(64,219,241,0.08)] animate-pulse" style={{ width: `${60 + (j * 10) % 30}%` }} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  )
}
